import React from 'react';
import { useNavigate } from 'react-router-dom';
import useAnalytics from '../hooks/useAnalytics';
import useScrollPosition from '../hooks/useScrollPosition';
import { LiveIcon, GitHubIcon } from './Icons';

const ProjectCard = React.memo(({ project, featured = false, detailed = false }) => {
    const { trackProjectView, trackClick } = useAnalytics();
    const navigate = useNavigate();
    const { saveScrollPosition } = useScrollPosition();

    const handleProjectClick = () => {
        saveScrollPosition('homepage');
        trackProjectView(project.title, project.id);
        navigate(`/projects/${project.id}`);
    };

    const handleLinkClick = (linkType, url) => {
        trackClick(`project_${linkType}`, {
            project_title: project.title,
            project_id: project.id,
            url: url
        });
    };

    // Featured large card
    if (featured) {
        return (
            <div
                className="group cursor-pointer bg-white border border-border-light rounded-2xl overflow-hidden hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300"
                onClick={handleProjectClick}
            >
                <div className="grid md:grid-cols-2 gap-0">
                    {/* Image */}
                    <div className="aspect-[4/3] md:aspect-auto bg-cream-100 overflow-hidden">
                        <img
                            src={project.cardImage || project.image}
                            alt={project.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                    </div>

                    {/* Content */}
                    <div className="p-6 md:p-8 flex flex-col justify-between">
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <span className="px-2.5 py-1 bg-coral-100 text-coral-600 text-xs font-medium rounded-full">
                                    Featured
                                </span>
                                {project.year && (
                                    <span className="text-xs text-charcoal-400">{project.year}</span>
                                )}
                            </div>

                            <h3 className="font-serif text-2xl text-charcoal-900 mb-3 group-hover:text-coral-600 transition-colors">
                                {project.title}
                            </h3>

                            <p className="text-charcoal-600 mb-4 line-clamp-3">
                                {project.description}
                            </p>

                            <div className="flex flex-wrap gap-2 mb-6">
                                {project.technologies.slice(0, 4).map((tech, index) => (
                                    <span
                                        key={index}
                                        className="px-2.5 py-1 bg-cream-100 text-charcoal-700 text-xs rounded-full"
                                    >
                                        {tech}
                                    </span>
                                ))}
                                {project.technologies.length > 4 && (
                                    <span className="px-2.5 py-1 bg-cream-100 text-charcoal-500 text-xs rounded-full">
                                        +{project.technologies.length - 4}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Links */}
                        <div className="flex items-center gap-4 pt-4 border-t border-border-light">
                            {project.github && (
                                <a
                                    href={project.github}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleLinkClick('github', project.github);
                                    }}
                                    className="flex items-center gap-1.5 text-sm text-charcoal-500 hover:text-charcoal-900 transition-colors"
                                >
                                    <GitHubIcon className="w-4 h-4" />
                                    <span>Code</span>
                                </a>
                            )}
                            {project.live && !project.playstore && !project.appstore && (
                                <a
                                    href={project.live}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleLinkClick('live', project.live);
                                    }}
                                    className="flex items-center gap-1.5 text-sm text-charcoal-500 hover:text-charcoal-900 transition-colors"
                                >
                                    <LiveIcon className="w-4 h-4" />
                                    <span>Live</span>
                                </a>
                            )}
                            {project.playstore && (
                                <a
                                    href={project.playstore}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleLinkClick('playstore', project.playstore);
                                    }}
                                    className="flex items-center gap-1.5 text-sm text-charcoal-500 hover:text-charcoal-900 transition-colors"
                                >
                                    <img src="/images/projects/google-play.svg" alt="" className="w-4 h-4" />
                                    <span>Play Store</span>
                                </a>
                            )}
                            {project.appstore && (
                                <a
                                    href={project.appstore}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleLinkClick('appstore', project.appstore);
                                    }}
                                    className="flex items-center gap-1.5 text-sm text-charcoal-500 hover:text-charcoal-900 transition-colors"
                                >
                                    <img src="/images/projects/apple-store-app.svg" alt="" className="w-4 h-4" />
                                    <span>App Store</span>
                                </a>
                            )}
                            <span className="ml-auto text-charcoal-400 group-hover:text-coral-500 transition-colors">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                </svg>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Standard card
    return (
        <div
            className="group cursor-pointer bg-white border border-border-light rounded-2xl overflow-hidden hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 h-full flex flex-col"
            onClick={handleProjectClick}
        >
            {/* Image */}
            <div className="aspect-[16/10] bg-cream-100 overflow-hidden">
                <img
                    src={project.cardImage || project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
            </div>

            {/* Content */}
            <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-2">
                    {project.year && (
                        <span className="text-xs text-charcoal-400">{project.year}</span>
                    )}
                </div>

                <h3 className="font-serif text-lg text-charcoal-900 mb-2 group-hover:text-coral-600 transition-colors">
                    {project.title}
                </h3>

                <p className="text-sm text-charcoal-600 mb-4 line-clamp-2 flex-1">
                    {project.description}
                </p>

                <div className="flex flex-wrap gap-1.5 mb-4">
                    {project.technologies.slice(0, 3).map((tech, index) => (
                        <span
                            key={index}
                            className="px-2 py-0.5 bg-cream-100 text-charcoal-600 text-xs rounded-full"
                        >
                            {tech}
                        </span>
                    ))}
                    {project.technologies.length > 3 && (
                        <span className="px-2 py-0.5 bg-cream-100 text-charcoal-400 text-xs rounded-full">
                            +{project.technologies.length - 3}
                        </span>
                    )}
                </div>

                {/* Links */}
                <div className="flex items-center gap-3 pt-3 border-t border-border-light mt-auto">
                    {project.github && (
                        <a
                            href={project.github}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleLinkClick('github', project.github);
                            }}
                            className="text-xs text-charcoal-500 hover:text-charcoal-900 transition-colors"
                        >
                            GitHub
                        </a>
                    )}
                    {project.live && !project.playstore && !project.appstore && (
                        <a
                            href={project.live}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleLinkClick('live', project.live);
                            }}
                            className="text-xs text-charcoal-500 hover:text-charcoal-900 transition-colors"
                        >
                            Live
                        </a>
                    )}
                    {project.playstore && (
                        <a
                            href={project.playstore}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleLinkClick('playstore', project.playstore);
                            }}
                            className="flex items-center gap-1 text-xs text-charcoal-500 hover:text-charcoal-900 transition-colors"
                        >
                            <img src="/images/projects/google-play.svg" alt="" className="w-3.5 h-3.5" />
                            <span>Play Store</span>
                        </a>
                    )}
                    {project.appstore && (
                        <a
                            href={project.appstore}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleLinkClick('appstore', project.appstore);
                            }}
                            className="flex items-center gap-1 text-xs text-charcoal-500 hover:text-charcoal-900 transition-colors"
                        >
                            <img src="/images/projects/apple-store-app.svg" alt="" className="w-3.5 h-3.5" />
                            <span>App Store</span>
                        </a>
                    )}
                    <span className="ml-auto text-charcoal-400 group-hover:text-coral-500 transition-colors">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                    </span>
                </div>
            </div>
        </div>
    );
});

export default ProjectCard;
